// Tour step properties:
//   - aboveDrawer: (bool) step targets an element inside a drawer overlay — adjusts z-index accordingly
//   - awaitClick: (string) CSS selector — the tour pauses until the user clicks this element
//   - awaitClickAdvanceDelay: (number) ms to wait after the click before moving to the next step
//   - awaitClickAll: (bool) listen for click on ALL elements matching awaitClick, not just the first
//   - awaitClickDelegate: (bool) use event delegation for awaitClick — resilient to DOM re-renders (e.g. FullCalendar)
//   - awaitClickLast: (bool) target the last matching element and block clicks on all others
//   - blockClickOnlySelectors: (string[]) CSS selectors whose click events are blocked (mousedown allowed for drag-and-drop)
//   - blockClickSelectors: (string[]) CSS selectors whose click AND mousedown events are blocked
//   - blockTarget: (bool|string) true = block clicks on the spotlight target; string = block clicks on that selector
//   - blockTargetException: (string|string[]) inside a blocked target, these selectors stay clickable
//   - blockTargetSiblings: (bool) block all siblings inside the target container, only the awaitClick element stays clickable
//   - congrats: (bool) marks the final congratulatory step (triggers confetti)
//   - content: (string) i18n key for the step body text
//   - hideFinish: (bool) hide the Finish button (used when chaining to another tour)
//   - hidePrev: (bool) hide the Previous button for this step
//   - highlightDays: (bool) add a pulsing highlight to calendar day cells
//   - nextTour: (string) id of the tour to start when this step or tour ends (chaining)
//   - noSpotlight: (bool) disable the spotlight cutout (full overlay, e.g. for congrats)
//   - position: (string) tooltip placement relative to the target — top | bottom | left | right | center
//   - pulse: (bool) show a pulsing animation on the awaitClick element (default: true when awaitClick is set)
//   - raiseAncestors: (bool) raise ancestor stacking contexts so the target escapes its parent z-index
//   - scrollTo: (string) 'top' scrolls the page to top instead of centering the target
//   - skipIfPresent: (string) CSS selector — if this element exists in the DOM when the step loads, auto-advance immediately
//   - spotlightPadding: (number) extra px around the target in the spotlight cutout
//   - target: (string) CSS selector of the element to highlight
//   - title: (string) i18n key for the step title
//   - watchTargets: (string[]) extra CSS selectors included in the spotlight area

export default {
  id: 'moderation',
  route: 'moderation',
  steps: [
    // --- Step 1: Overview of the moderation section ---
    {
      target: '.section-moderation',
      title: 'tours.moderation.steps.overview.title',
      content: 'tours.moderation.steps.overview.content',
      position: 'center',
      spotlightPadding: { top: 40, right: -350, bottom: 40, left: 40 },
      blockTarget: true,
      blockOutsideSpotlight: true
    },
    // --- Step 2: Select all pages ---
    {
      target: '.section-moderation .filter-selected',
      title: 'tours.moderation.steps.selectAll.title',
      content: 'tours.moderation.steps.selectAll.content',
      position: 'right',
      spotlightPadding: 6,
      awaitClick: '.section-moderation .filter-selected .hasTooltip.enable',
      awaitClickAdvanceDelay: 600,
      skipIfPresent: '.section-moderation .filter-selected img[src*="unselect_icon"]',
      pulse: false,
      blockOutsideSpotlight: true
    },
    // --- Step 3: Pages sidebar overview ---
    // {
    //   target: '.section-moderation .pages-sidebar',
    //   title: 'tours.moderation.steps.sidebar.title',
    //   content: 'tours.moderation.steps.sidebar.content',
    //   position: 'right',
    //   spotlightPadding: 6,
    //   hidePrev: true,
    //   blockTarget: true
    // },
    // --- Step 4: Inbox area ---
    {
      target: '.section-moderation .tables-wrapper',
      title: 'tours.moderation.steps.inbox.title',
      content: 'tours.moderation.steps.inbox.content',
      position: 'center',
      spotlightPadding: 20,
      blockTarget: true,
      hidePrev: true,
      blockOutsideSpotlight: true
    },
    // --- Step 5: Search bar and filter ---
    {
      target: '.section-moderation .partie-inbox .header .cta',
      title: 'tours.moderation.steps.searchFilter.title',
      content: 'tours.moderation.steps.searchFilter.content',
      position: 'bottom',
      spotlightPadding: 8,
      watchTargets: ['.container-filter'],
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 6: Add-user / assign button ---
    {
      target: '.section-moderation .events .event .footer .add-user',
      title: 'tours.moderation.steps.addUser.title',
      content: 'tours.moderation.steps.addUser.content',
      position: 'top',
      spotlightPadding: 6,
      blockTarget: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 7: Notation / evaluation ---
    {
      target: '.section-moderation .events .event .footer .notation',
      title: 'tours.moderation.steps.notation.title',
      content: 'tours.moderation.steps.notation.content',
      position: 'top',
      spotlightPadding: 6,

      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 8: Detail event section ---
    {
      target: '.section-moderation .detail-event',
      title: 'tours.moderation.steps.detailEvent.title',
      content: 'tours.moderation.steps.detailEvent.content',
      position: 'left',
      spotlightPadding: 8,
      blockTarget: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 9: Status action dropdown ---
    {
      target: '.section-moderation .detail-event .selectActionEvent',
      title: 'tours.moderation.steps.selectAction.title',
      content: 'tours.moderation.steps.selectAction.content',
      position: 'left',
      spotlightPadding: 6,
      watchTargets: ['.listOptions'],
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 10: Post content & actions ---
    // {
    //   target: '.section-moderation .detail-event .bar-action-container',
    //   title: 'tours.moderation.steps.postActions.title',
    //   content: 'tours.moderation.steps.postActions.content',
    //   position: 'top',
    //   spotlightPadding: 6,
    //   blockTarget: true
    // },
    // --- Step 11: Comments area ---
    {
      target: '.section-moderation .detail-event .container-content-comments',
      title: 'tours.moderation.steps.comments.title',
      content: 'tours.moderation.steps.comments.content',
      position: 'left',
      spotlightPadding: 8,
      blockTarget: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Congrats ---
    {
      title: 'tours.moderation.steps.congrats.title',
      content: 'tours.moderation.steps.congrats.content',
      position: 'center',
      noSpotlight: true,
      congrats: true,
      hideFinish: true,
      nextTour: 'bots',
      blockOutsideSpotlight: true
    }
  ]
}
